// useCanvasViewport — React-hook som tracker scroll-position + viewport-
// storlek för WorkflowCanvas. Används av minimap + panel-beräkningar.
// RAF-throttling förhindrar att state setts många gånger per frame vid
// snabba trackpad-pans.
//
// Extraherad från WorkflowCanvas.tsx (A-008) för att minska filens
// storlek + göra scroll/resize-logiken enhets-testbar.

import { useEffect, useState, type RefObject } from 'react';

export interface CanvasViewportState {
  scrollPosition: { left: number; top: number };
  viewportSize: { width: number; height: number };
}

export function useCanvasViewport(
  canvasRef: RefObject<HTMLDivElement>,
): CanvasViewportState {
  const [scrollPosition, setScrollPosition] = useState({ left: 0, top: 0 });
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // RAF-throttle så minimap inte drivs många gånger per frame
    // (rå scroll fires många gånger/sek på macOS trackpads).
    let rafId: number | null = null;
    const update = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        setScrollPosition({ left: canvas.scrollLeft, top: canvas.scrollTop });
        setViewportSize({ width: canvas.clientWidth, height: canvas.clientHeight });
      });
    };
    update();
    canvas.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      canvas.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [canvasRef]);

  return { scrollPosition, viewportSize };
}
